import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { gamePlayService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { ROUTES } from '../../constants/routes';
import Spinner from '../../components/ui/Spinner';
import logoImg from '../../assets/images/Transparent_logo.png';
import {
  CheckCircleIcon,
  StarIcon,
  ClockIcon,
  HelpCircleIcon,
  ShareIcon,
} from '../../components/common/Icons';


const setDisplayTime = (timeInSeconds = 0) => {
  const m = Math.floor(timeInSeconds / 60);
  const s = timeInSeconds % 60;
  const oM = m < 10 ? `0${m}` : `${m}`;
  const oS = s < 10 ? `0${s}` : `${s}`;
  return `${oM}:${oS} min`;
};

const GameResultsPage = () => {
  const { seasonId, quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();

  useEffect(() => {
    if (!user) {
      navigate(ROUTES.PUBLIC.HOME, { replace: true });
    }
  }, [user, navigate]);

  const stateData = location.state || {};
  const [score, setScore] = useState(stateData.score ?? null);
  const [numQuestions, setNumQuestions] = useState(stateData.numQuestions ?? null);
  const [timeTaken, setTimeTaken] = useState(stateData.timeTaken ?? null);
  const [quizTitle, setQuizTitle] = useState(stateData.quizTitle || 'Bible Quiz');
  const [leaderboardViewData, setLeaderboardViewData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      try {
        const [lbSettings, quizDetails] = await Promise.all([
          gamePlayService.getSeasonLeaderboardSetting(seasonId),
          gamePlayService.getQuizDetails(seasonId, quizId),
        ]);

        if (isMounted) {
          if (lbSettings && lbSettings.appearance) {
            setLeaderboardViewData(lbSettings.appearance);
          }
          if (quizDetails?.title) {
            setQuizTitle(quizDetails.title);
          }
        }

        // If score not in location.state (direct page reload), fetch user result
        if (score === null && user?.uid) {
          const res = await gamePlayService.getUserResult(seasonId, quizId, user.uid);
          if (isMounted && res) {
            setScore(res.score ?? 0);
            setNumQuestions(res.numQuestions ?? 10);
            setTimeTaken(res.timeTaken ?? 0);
            if (res.title) setQuizTitle(res.title);
          }
        }
      } catch (err) {
        console.error('Failed to load results metadata:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [seasonId, quizId, user, score]);

  const correctScore = score || 0;
  const totalQs = numQuestions || 10;
  const accuracy = totalQs > 0 ? (correctScore / totalQs) * 100 : 0;
  const accuracyStr = `${Math.round(accuracy)}%`;
  const timeStr = setDisplayTime(timeTaken || 0);

  const celebrationTitle =
    accuracy >= 80
      ? 'GLORY & EXCELLENCE!'
      : accuracy >= 50
      ? 'WELL DONE!'
      : 'FAITHFUL EFFORT!';


  const handleShare = async () => {
    const userName = user?.displayName ? `${user.displayName}: ` : '';
    const text = `🏆 ${userName}I scored ${correctScore}/${totalQs} (${accuracyStr}) in ${timeStr} on "${quizTitle}" on Bible Contest! Test your Scripture knowledge here: https://go.biblecontest.app/quiz/${seasonId}/${quizId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: quizTitle || 'Bible Contest Score',
          text,
          url: `https://go.biblecontest.app/quiz/${seasonId}/${quizId}`,
        });
      } catch {
        // User cancelled share
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        showToast('Score achievement copied to clipboard! Share with friends.', 'success');
      } catch {
        // Clipboard write failed
      }
    }
  };

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: '#FBF6EF',
          minHeight: '90vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#FBF6EF',
        minHeight: '90vh',
        padding: '24px 16px 36px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* App Bar matching ResultsScreen.dart */}
      <div style={{ textAlign: 'center', marginBottom: '24px', width: '100%', maxWidth: '480px' }}>
        <h1
          style={{
            fontFamily: 'BebasNeue, "Oswald", "Impact", sans-serif',
            fontSize: '24px',
            color: '#332E2A',
            letterSpacing: '1.5px',
            fontWeight: 'bold',
            margin: 0,
            textTransform: 'uppercase',
          }}
        >
          QUIZ COMPLETED
        </h1>
      </div>

      <div style={{ width: '100%', maxWidth: '440px' }}>
        {leaderboardViewData === 'after_quiz_finished' ? (
          /* Faithful Attempt (After Quiz Finished) Card */
          <div>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                border: '1px solid rgba(51, 46, 42, 0.08)',
                padding: '24px',
                textAlign: 'center',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
              }}
            >
              {/* Stylized hourglass icon header */}
              <div
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(192, 133, 82, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '36px',
                }}
              >
                ⏳
              </div>

              <h2
                style={{
                  fontFamily: 'BebasNeue, "Oswald", "Impact", sans-serif',
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: '#332E2A',
                  letterSpacing: '1.0px',
                  margin: '0 0 16px',
                  textTransform: 'uppercase',
                }}
              >
                FAITHFUL ATTEMPT!
              </h2>

              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '14px 0' }} />

              <ResultRow icon="❓" label="Total Questions" value={totalQs.toString()} />

              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '14px 0' }} />

              <p
                style={{
                  fontSize: '14px',
                  color: 'rgba(51, 46, 42, 0.7)',
                  lineHeight: 1.5,
                  margin: '16px 0 24px',
                }}
              >
                Your answers are recorded! Final scores & leaderboard will be revealed when the quiz
                window closes.
              </p>

              <button
                type="button"
                onClick={() => navigate(`/play/season/${seasonId}`)}
                style={{
                  width: '100%',
                  height: '48px',
                  backgroundColor: '#FF5C56',
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease',
                }}
              >
                Back to Quizzes
              </button>
            </div>
          </div>
        ) : (
          /* Normal Results Card matching ResultsScreen.dart */
          <div>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                border: '1px solid rgba(51, 46, 42, 0.08)',
                padding: '24px',
                textAlign: 'center',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                marginBottom: '16px',
              }}
            >
              {/* Stylized trophy icon header */}
              <div
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(192, 133, 82, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '36px',
                }}
              >
                🏆
              </div>

              <h2
                style={{
                  fontFamily: 'BebasNeue, "Oswald", "Impact", sans-serif',
                  fontSize: '26px',
                  fontWeight: 'bold',
                  color: '#332E2A',
                  letterSpacing: '1.0px',
                  margin: '0 0 16px',
                  textTransform: 'uppercase',
                }}
              >
                {celebrationTitle}
              </h2>

              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />
              <ResultRow
                icon={<HelpCircleIcon size={18} style={{ color: '#64748B' }} />}
                label="Total Questions"
                value={totalQs.toString()}
              />

              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />
              <ResultRow
                icon={<CheckCircleIcon size={18} style={{ color: '#059669' }} />}
                label="Correct Answers"
                value={correctScore.toString()}
              />

              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />
              <ResultRow
                icon={<StarIcon size={18} style={{ color: '#D97706' }} />}
                label="Accuracy"
                value={accuracyStr}
              />

              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />
              <ResultRow
                icon={<ClockIcon size={18} style={{ color: '#FF5C56' }} />}
                label="Time Taken"
                value={timeStr}
              />

              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '16px 0 12px' }} />

              {/* Branding Footer */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  paddingTop: '4px',
                }}
              >
                <img
                  src={logoImg}
                  alt="Bible Contest"
                  style={{ height: '24px', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <span
                  style={{
                    fontFamily: 'BebasNeue, "Oswald", "Impact", sans-serif',
                    fontSize: '16px',
                    color: '#332E2A',
                    letterSpacing: '1.0px',
                    fontWeight: 'bold',
                  }}
                >
                  BIBLE CONTEST
                </span>
              </div>
            </div>

            {/* Action Buttons Row matching ResultsScreen.dart */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => navigate(`/play/season/${seasonId}`)}
                style={{
                  flex: 1,
                  height: '48px',
                  backgroundColor: 'transparent',
                  color: '#332E2A',
                  border: '1.5px solid rgba(51, 46, 42, 0.15)',
                  borderRadius: '16px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
              >
                Back to Quizzes
              </button>

              <button
                type="button"
                onClick={handleShare}
                style={{
                  flex: 1,
                  height: '48px',
                  backgroundColor: '#FF5C56',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '16px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(255, 92, 86, 0.3)',
                  transition: 'transform 0.15s ease',
                }}
              >
                <ShareIcon size={16} />
                <span>Share Score</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ResultRow = ({ icon, label, value }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '4px 0',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      <span style={{ color: 'rgba(51, 46, 42, 0.55)', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </span>
    </div>
    <span style={{ color: '#332E2A', fontSize: '14px', fontWeight: 'bold' }}>{value}</span>
  </div>
);


export default GameResultsPage;
